import React from 'react';
import { Star } from 'lucide-react';
import type { Recurso } from '../../types';
import { Modal } from '../ui/Modal';
import { Boton } from '../ui/Boton';
import { Insignia } from '../ui/Etiquetas';
import { Aviso } from '../ui/Aviso';
interface Props {
  recurso: Recurso | null;
  abierto: boolean;
  onCerrar: () => void;
  favorito: boolean;
  onFavorito: () => void;
  onAgregarAClase?: () => void;
  onCopiar?: () => void;
  recursos: Recurso[];
  onAbrirRelacionado: (r: Recurso) => void;
}
function relacionados(recurso: Recurso, recursos: Recurso[]): Recurso[] {
  const referencias = new Set<string>();
  const texto = [...recurso.prerrequisitos, recurso.regresion, recurso.progresion].join(' ');
  const encontrados = texto.match(/[AFTLIESCV]\d{2}/g) || [];
  encontrados.forEach(id => referencias.add(id));
  const porReferencia = recursos.filter(r => referencias.has(r.id) && r.id !== recurso.id);
  const porFamilia = recursos.filter(r => r.familia === recurso.familia && r.id !== recurso.id && !referencias.has(r.id)).slice(0, 4);
  return [...porReferencia, ...porFamilia].slice(0, 8);
}
export function FichaRecurso({
  recurso,
  abierto,
  onCerrar,
  favorito,
  onFavorito,
  onAgregarAClase,
  onCopiar,
  recursos,
  onAbrirRelacionado
}: Props) {
  if (!recurso) return null;
  const vinculados = relacionados(recurso, recursos);
  return <Modal abierto={abierto} onCerrar={onCerrar} titulo={`${recurso.id} · ${recurso.nombre}`} descripcion={recurso.alias.length ? `Alias: ${recurso.alias.join(' · ')}` : undefined} acciones={<>
          <Boton variante="contorno" onClick={onFavorito} data-id="element-291" data-mp="2nfqa06sjk1ag">
            <Star className={`h-4 w-4 ${favorito ? 'fill-[#FACC15] text-[#EAB308]' : ''}`} aria-hidden="true" data-id="element-292" data-mp="2ne5fgh688icl" />
            {favorito ? 'Quitar de favoritos' : 'Favorito'}
          </Boton>
          {onCopiar && <Boton variante="secundario" onClick={onCopiar} data-id="element-293" data-mp="28kaedxcbbxby">
              Crear copia personalizada
            </Boton>}
          {onAgregarAClase && <Boton onClick={onAgregarAClase} data-id="element-294" data-mp="23uq27ynn7lny">Agregar a clase activa</Boton>}
        </>} data-id="element-290" data-mp="31aaivv2wj8ea">
      <div className="space-y-4" data-id="element-295" data-mp="23sde13rakf2g">
        <div className="flex flex-wrap gap-1.5" data-id="element-296" data-mp="1pvqfiwjoxguf">
          <Insignia tono="vino" data-id="element-297" data-mp="1px1jawd4yolj">{recurso.familia}</Insignia>
          <Insignia data-id="element-298" data-mp="1txlcxpyi3a2x">Nivel mínimo: {recurso.nivelMinimo}</Insignia>
          <Insignia data-id="element-299" data-mp="2hmedy0tzc84e">Recomendado: {recurso.nivelesRecomendados.join(' · ')}</Insignia>
          <Insignia tono="azul" data-id="element-300" data-mp="2hoh3ykv4u194">{recurso.entornos.join(' · ')}</Insignia>
          <Insignia data-id="element-301" data-mp="2mkfvghbrbzdr">Intensidad {recurso.intensidad}</Insignia>
          <Insignia data-id="element-302" data-mp="28um6lz095f41">
            {recurso.duracionMin}–{recurso.duracionMax} min
          </Insignia>
          {recurso.bilateral && <Insignia tono="violeta" data-id="element-303" data-mp="2ctuzz2rdraz2">Bilateral</Insignia>}
          {recurso.aptoMultinivel && <Insignia tono="verde" data-id="element-304" data-mp="30jxoj8w35ysx">Apto multinivel</Insignia>}
          {recurso.dinamico && <Insignia tono="alerta" data-id="element-305" data-mp="35c8r6ji7nyp6">Dinámico o caída</Insignia>}
        </div>

        {recurso.requiereEvaluacionDocente && <Aviso tipo="aviso" titulo="Requiere evaluación docente" data-id="element-306" data-mp="tvf6vyqp9rqm">
            La decisión de incluirlo depende de tu evaluación del grupo y del espacio.
          </Aviso>}

        <Bloque titulo="Objetivo" texto={recurso.objetivo} data-id="element-307" data-mp="13mt5836x0li6" />
        <Bloque titulo="Capacidades" texto={recurso.capacidades.join(' · ')} data-id="element-308" data-mp="ynggfcwhh4cy" />
        <Bloque titulo="Prerrequisitos" texto={recurso.prerrequisitos.join(' · ')} data-id="element-309" data-mp="ypjgkprcodve" />
        <Bloque titulo="Regresión" texto={recurso.regresion} data-id="element-310" data-mp="aydz665rhdiz" />
        <Bloque titulo="Progresión" texto={recurso.progresion} data-id="element-311" data-mp="5zkmgfyatqjv" />
        <Bloque titulo="Error frecuente / clave docente" texto={recurso.claveDocente} data-id="element-312" data-mp="615i68wcnfjb" />
        <Bloque titulo="Seguridad" texto={recurso.seguridad} data-id="element-313" data-mp="ktyy1vfj9xoi" />

        {vinculados.length > 0 && <div data-id="element-314" data-mp="fz1ngk2lvg9i">
            <h3 className="text-sm font-bold text-tinta-titulo" data-id="element-315" data-mp="1wb70ib6md3q1">Relacionados</h3>
            <ul className="mt-2 flex flex-wrap gap-2" data-id="element-316" data-mp="1wdbc7farf70v">
              {vinculados.map(r => <li key={r.id} data-id="element-317" data-mp="1re87xx2lt4ty">
                  <button type="button" onClick={() => onAbrirRelacionado(r)} className="min-h-[44px] rounded-xl border border-linea px-3 text-sm font-semibold text-vino hover:border-vino hover:bg-vino/5" data-id="element-318" data-mp="vjsubjsm7gi">
                    {r.id} · {r.nombre}
                  </button>
                </li>)}
            </ul>
          </div>}
      </div>
    </Modal>;
}
function Bloque({
  titulo,
  texto
}: {
  titulo: string;
  texto: string;
}) {
  return <div className="rounded-xl border border-linea bg-lienzo p-3" data-id="element-319" data-mp="oreefxnxkeou">
      <h3 className="text-xs font-bold uppercase tracking-wide text-tinta-tenue" data-id="element-320" data-mp="tjfmt8hz9l9l">{titulo}</h3>
      <p className="mt-1 text-sm text-tinta" data-id="element-321" data-mp="xlsptp5cb52o">{texto}</p>
    </div>;
}